import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { UrlService } from '../../../../environments/urlService';
import { FundPerformance } from '../../fund-performance/dto/FundPerformance';
import { Observable } from 'rxjs';
import { PortfolioPerformance, PortfolioPerformanceDTO } from '../dto/PortfolioPerformance';
import { IS_SPINNER_ACTIVE } from '../../../util/interceptors';
import { PpAnalysisWaacDTO } from '../component/sub/appreciation-at-cost/dtos';
import { InitialFollowOnTableDTO } from '../component/sub/initial-follow-on-investment/pojo/initial-follow-on-classes';
import { OrganizationOverviewDTO } from '../../organization/dto/OrganizationOverviewDTO';
import { SortedPP } from '../component/sub/pp-overview/portfolio-summary-table/dto/SortedPPDTOs';

@Injectable({
  providedIn: 'root',
})
export class PortfolioPerformanceBackendService {
  private readonly url: string;

  constructor(private http: HttpClient,
    private urlService: UrlService,
  ) {
    this.url = this.urlService.getFmsUrl() + 'portfolio-performance/';
  }

  getAll(fundUUID: string, date: Date, showSpinner?: boolean): Promise<PortfolioPerformanceDTO[]> {
    const period: string = date.toString();
    return this.http.get<PortfolioPerformanceDTO[]>(this.url, {
      params: { fundUUID, period },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, showSpinner),
      withCredentials: true,
    }).toPromise();
  }

  getAllByFund(fundUUID: string): Observable<PortfolioPerformanceDTO[]> {
    return this.http.get<PortfolioPerformanceDTO[]>(this.url + 'fund', {
      params: { fundUUID },
      withCredentials: true,
    });
  }

  getPPAmount(fundUUID: string, date: Date): Promise<number> {
    const period: string = date.toString();
    return this.http.get<number>(this.url + 'amount', {
      params: { fundUUID, period },
      withCredentials: true,
    }).toPromise();
  }

  getAllSorted(fundUUID: string, date: Date): Promise<SortedPP> {
    const period: string = date.toString();
    return this.http.get<SortedPP>(this.url + 'sorted', {
      params: { fundUUID, period },
      withCredentials: true,
    }).toPromise();
  }

  getOverview(orgUUID: string): Promise<OrganizationOverviewDTO[]> {
    return this.http.get<OrganizationOverviewDTO[]>(this.url + 'overview', {
      params: { orgUUID },
      withCredentials: true,
    }).toPromise();
  }

  getInitialFollowOnTableData(fundUUID: string, date: Date): Observable<InitialFollowOnTableDTO> {
    const period: string = date.toString();
    return this.http.get<InitialFollowOnTableDTO>(this.url + 'initial-follow-on', {
      params: { fundUUID, period },
      withCredentials: true,
    });
  }

  getAppreciationAtCost(fundId: number, date: Date): Observable<PpAnalysisWaacDTO[]> {
    const period: string = date.toString();
    return this.http.get<PpAnalysisWaacDTO[]>(this.url + 'appreciation-at-cost', {
      params: { fundId, period },
      withCredentials: true,
    });
  }

  getPeriods(fundUUID: string, companyId: number): Promise<Date[]> {
    return this.http.get<Date[]>(this.url + 'periods', {
      params: { fundUUID, companyId },
      withCredentials: true,
    }).toPromise();
  }

  getPortfolioPerformancesByCompany(fundUUID: string, companyId: number): Promise<PortfolioPerformance[]> {
    return this.http.get<PortfolioPerformance[]>(this.url + 'company', {
      params: { fundUUID, companyId },
      withCredentials: true,
    }).toPromise();
  }

  getCompanyHasAtLeastOnePP(fundUUID: string, companyId: number): Promise<boolean> {
    return this.http.get<boolean>(this.url + 'company/' + companyId + '/exists', {
      params: { fundUUID },
      withCredentials: true,
    }).toPromise();
  }

  updateCommentInFundPerformance(portfolioId: number, description: string): Promise<FundPerformance> {
    return this.http.put<FundPerformance>(this.url + 'comment', null, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      params: { portfolioId, description },
      withCredentials: true,
    }).toPromise();
  }

  setTotalCommitment(totalCommitment: number, ppId: number): Observable<void> {
    const url = `${this.url}total-commitment/${ppId}`;
    return this.http.patch<void>(url, totalCommitment, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  updatePPCompanyGroup(ppUUID: string, group: string): Promise<void> {
    return this.http.put<void>(this.url + 'group', {}, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      params: { ppUUID, group },
      withCredentials: true,
    }).toPromise();
  }

  getFundsIdsByCompanyId(companyId: number): Observable<string[]> {
    return this.http.get<string[]>(this.url + 'funds-uuids-by-company-id', {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      params: { companyId },
      withCredentials: true,
    });
  }

  updatePP(pp: PortfolioPerformance): Observable<void> {
    return this.http.put<void>(this.url, pp, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }
}
